import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, CircularProgress } from '@mui/material';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import { AutocompleteCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { showMessageAction } from 'store/global/actions';
import {
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
  linkConditionTasksAction,
} from 'store/loanApplications/actions';
import { selectTasks } from 'store/loanApplications/selectors';
import { selectTaskTypeDynamicField } from 'store/system/selectors';

import { ConditionsTestIds } from '../../Conditions.testIds';

import { LINK_CONDITION_TASKS_FORM_ID } from './LinkConditionTasksDrawer.constants';
import { formStyles, submitButtonContentStyles } from './LinkConditionTasksDrawer.styles';
import { getLinkableTaskOptions, getLinkedTasksMessage } from './LinkConditionTasksDrawer.utils';

const LinkConditionTasksDrawer = ({ open, condition, loanId, onClose }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const tasks = useSelector(selectTasks);
  const dynamicFieldOptions = useSelector(selectTaskTypeDynamicField);

  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const taskOptions = useMemo(
    () =>
      getLinkableTaskOptions({
        tasks,
        linkedTaskIds: condition.taskIds ?? [],
        dynamicFieldOptions,
      }),
    [condition.taskIds, dynamicFieldOptions, tasks],
  );

  const hasLinkableTasks = !!taskOptions.length;
  const isSubmitDisabled = !selectedTasks.length || isSaving;
  const drawerTitle = condition.canonicalTitle
    ? `Link Tasks · ${condition.canonicalTitle}`
    : `Link Tasks to Condition #${condition.number}`;

  const onTasksChange = useCallback((_event, value) => {
    setSelectedTasks(value ?? []);
  }, []);

  const refreshConditionAndTasks = useCallback(
    () =>
      Promise.all([
        dispatch(getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true })),
        dispatch(getLoanApplicationTasksAction({ applicationId })),
      ]),
    [applicationId, dispatch, loanId],
  );

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      if (isSubmitDisabled) {
        return;
      }

      setIsSaving(true);

      dispatch(
        linkConditionTasksAction({
          loanConditionId: condition.id,
          taskIds: selectedTasks.map(({ id }) => id),
        }),
      )
        .then(refreshConditionAndTasks)
        .then(() =>
          dispatch(
            showMessageAction({
              message: getLinkedTasksMessage({
                count: selectedTasks.length,
                conditionNumber: condition.number,
              }),
              variant: 'success',
            }),
          ),
        )
        .then(onClose)
        .finally(() => setIsSaving(false));
    },
    [
      condition.id,
      condition.number,
      dispatch,
      isSubmitDisabled,
      onClose,
      refreshConditionAndTasks,
      selectedTasks,
    ],
  );

  const content = (
    <Box
      noValidate
      component="form"
      id={LINK_CONDITION_TASKS_FORM_ID}
      sx={formStyles}
      onSubmit={onSubmit}
    >
      <AutocompleteCDS
        multiple
        dataTestId={ConditionsTestIds.LINK_TASKS_SELECT}
        disabled={isSaving || !hasLinkableTasks}
        helperText={
          hasLinkableTasks ? null : 'Every task on this loan is already on this condition.'
        }
        label="Tasks on this loan"
        name="tasks"
        optionKey="id"
        options={taskOptions}
        value={selectedTasks}
        onChange={onTasksChange}
      />
    </Box>
  );

  const actions = (
    <Button
      data-testid={ConditionsTestIds.LINK_TASKS_SUBMIT_BUTTON}
      disabled={isSubmitDisabled}
      form={LINK_CONDITION_TASKS_FORM_ID}
      type="submit"
      variant="contained"
    >
      <Box sx={submitButtonContentStyles}>
        {isSaving && <CircularProgress color="inherit" size={16} />}
        Link
      </Box>
    </Button>
  );

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      data-testid={ConditionsTestIds.LINK_TASKS_DRAWER}
      isDirty={!!selectedTasks.length}
      isLoading={isSaving}
      open={open}
      size={DrawerSize.SMALL}
      title={drawerTitle}
      onClose={onClose}
    />
  );
};

LinkConditionTasksDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  condition: PropTypes.object.isRequired,
  loanId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(LinkConditionTasksDrawer);
